import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TierBadge } from '@/components/dashboard/TierBadge';
import { formatLatency, formatCost, timeAgo } from '@/lib/utils';
import api from '@/lib/api';
import { 
  ScrollText, 
  ChevronLeft, 
  ChevronRight,
  Database,
  Shield,
  RefreshCw
} from 'lucide-react';

export function AuditPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [tierFilter, setTierFilter] = useState('all');
  const limit = 20;

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await api.getAuditLogs(
        limit, 
        page * limit, 
        tierFilter === 'all' ? null : tierFilter
      );
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, tierFilter]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="audit-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight">
            AUDIT LOGS
          </h1>
          <p className="text-muted-foreground mt-1">
            Complete inference decision history
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={tierFilter} onValueChange={(v) => { setTierFilter(v); setPage(0); }}>
            <SelectTrigger className="w-40" data-testid="tier-filter">
              <SelectValue placeholder="Filter by tier" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tiers</SelectItem>
              <SelectItem value="tier1_local_fast">Local Fast</SelectItem>
              <SelectItem value="tier2_local_smart">Local Smart</SelectItem>
              <SelectItem value="tier3_cloud">Cloud</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchLogs} data-testid="refresh-logs-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-border bg-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <ScrollText className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{total.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Total Log Entries</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Database className="h-8 w-8 text-emerald-400" />
              <div>
                <p className="text-2xl font-bold">
                  {logs.filter(l => l.cache_hit).length}
                </p>
                <p className="text-xs text-muted-foreground">Cache Hits (this page)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Shield className="h-8 w-8 text-amber-400" />
              <div>
                <p className="text-2xl font-bold">
                  {logs.filter(l => l.pii_detected).length}
                </p>
                <p className="text-xs text-muted-foreground">PII Detected (this page)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Logs Table */}
      <Card className="border-border bg-card">
        <CardHeader className="border-b border-border">
          <CardTitle className="text-sm font-medium">
            Inference Logs
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="text-xs font-mono">TIMESTAMP</TableHead>
                  <TableHead className="text-xs font-mono">TIER</TableHead>
                  <TableHead className="text-xs font-mono">INTENT</TableHead>
                  <TableHead className="text-xs font-mono">QUERY</TableHead>
                  <TableHead className="text-xs font-mono text-right">CONFIDENCE</TableHead>
                  <TableHead className="text-xs font-mono text-right">LATENCY</TableHead>
                  <TableHead className="text-xs font-mono text-right">TOKENS</TableHead>
                  <TableHead className="text-xs font-mono text-right">COST</TableHead>
                  <TableHead className="text-xs font-mono text-center">FLAGS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i} className="border-border">
                      {[...Array(9)].map((_, j) => (
                        <TableCell key={j}>
                          <div className="h-4 bg-secondary rounded animate-pulse" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : logs.length === 0 ? (
                  <TableRow className="border-border">
                    <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                      No audit logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.id} className="border-border hover:bg-secondary/30">
                      <TableCell className="text-xs font-mono text-muted-foreground">
                        {timeAgo(log.timestamp)}
                      </TableCell>
                      <TableCell>
                        <TierBadge tier={log.tier} size="sm" />
                      </TableCell>
                      <TableCell className="text-xs font-mono capitalize">
                        {log.intent}
                      </TableCell>
                      <TableCell className="text-xs max-w-[200px] truncate">
                        {log.query_preview}
                      </TableCell>
                      <TableCell className="text-xs font-mono text-right">
                        {(log.confidence * 100).toFixed(0)}%
                      </TableCell>
                      <TableCell className="text-xs font-mono text-right">
                        {formatLatency(log.latency_ms)}
                      </TableCell>
                      <TableCell className="text-xs font-mono text-right">
                        {log.tokens_input + log.tokens_output}
                      </TableCell>
                      <TableCell className="text-xs font-mono text-right">
                        {formatCost(log.cost)}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          {log.cache_hit && (
                            <span className="px-1.5 py-0.5 text-[10px] font-mono bg-emerald-500/20 text-emerald-400 rounded">
                              CACHE
                            </span>
                          )}
                          {log.pii_detected && (
                            <span className="px-1.5 py-0.5 text-[10px] font-mono bg-amber-500/20 text-amber-400 rounded">
                              PII
                            </span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <p className="text-xs text-muted-foreground">
              Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p - 1)}
                disabled={page === 0}
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs font-mono px-2">
                {page + 1} / {totalPages || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages - 1}
                data-testid="next-page-btn"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default AuditPage;
